/**
 * Bloque 4 — Config de deep links.
 *
 * Es lo que hace que un click en el panel caiga en el registro correcto del
 * sistema de origen. Se edita y **se verifica**: cada cambio rearma un link de
 * ejemplo con los builders del contrato, así se ve la URL exacta a la que va a
 * ir la gente antes de aplicar nada.
 *
 * Los ejemplos se renderizan como `<code>` con botón de copiar y **nunca como
 * `<a>`**: los ids son de muestra, así que el builder no puede devolver null y
 * el estado "sin vincular" no los cubre — un ancla acá sería el único link del
 * panel que garantiza un 404 en el sistema externo. El trabajo del bloque es
 * mostrar la URL, no navegarla.
 *
 * Sobre persistencia — decisión explícita: esta config la resuelve el Worker por
 * variable de entorno (`deepLinkConfigFromEnv`) y viaja al front en
 * `GET /api/config`. No hay endpoint ni schema en el contrato para escribirla, y
 * guardarla en base crearía una segunda fuente de verdad que puede contradecir al
 * entorno — justo el problema que el sobre `links` del servidor evita. Así que el
 * editor **no dice que guarda**: valida, muestra a dónde apuntaría cada link y
 * entrega las variables listas para aplicar en el entorno.
 */
import type { DeepLinkConfig } from '@ops/contracts'
import { useMemo, useState } from 'react'

import { buildersFor } from '../../lib/deeplinks'
import { listaEs, plural } from '../../lib/format'
import { useToast } from '../../shell/AppShell'
import {
  Button,
  Card,
  Icon,
  Panel,
  Rule,
  Section,
  Stack,
  StatusPill,
  TextField,
} from '../../ui'
import {
  CAMPOS,
  GRUPOS,
  camposVacios,
  conCampo,
  envSnippet,
  hayCambios,
  valorDeCampo,
  type CampoConfig,
  type CampoId,
} from './configCampos'

/**
 * Lleva el foco al campo que falta completar. El `id` del input lo genera
 * `useId()` adentro de `TextField`, así que el ancla es el `data-campo` que este
 * panel pone en el envoltorio: la acción del estado "no se puede armar" tiene
 * que dejar al usuario tipeando, no solo nombrarle la variable.
 */
function enfocarCampo(campo: CampoId): void {
  const control = document.querySelector<HTMLInputElement>(
    `[data-campo="${campo}"] input`,
  )
  control?.focus()
  control?.select()
}

export interface DeepLinksPanelProps {
  /** La config tal como la sirve el servidor. Es la referencia. */
  servidor: DeepLinkConfig
  id?: string
}

interface Ejemplo {
  que: string
  detalle: string
  /** Campos de los que depende este link. El primero vacío es el que lo rompe. */
  depende: readonly CampoId[]
}

/**
 * Ids de ejemplo, con **la forma real que tiene cada sistema** (`lead_` y `sub_`
 * de catorce caracteres, `lab_` de doce, `wi_` de veinte). No son registros que
 * existan: el bloque muestra la URL, no la abre, así que un id de mentira no
 * puede llevar a nadie a un 404. Tener la forma correcta importa igual, porque
 * este bloque es lo que enseña qué se guarda en cada campo.
 */
const ID_PLANE = 'wi_2p8xk1v0m4c7q3r5t9zb'
const ID_CLOSE = 'lead_r4aasagnpx4zr5'
const ID_STRIPE_SUB = 'sub_8kq3md2vf7wx1n'
const ID_PLATAFORMA = 'lab_1o2fb2xh2b4t'
const QUERY_SEO = 'agentes de whatsapp'

export function DeepLinksPanel({ servidor, id }: DeepLinksPanelProps) {
  const { push } = useToast()
  /** null = se está viendo la config del servidor, sin editar. */
  const [borrador, setBorrador] = useState<DeepLinkConfig | null>(null)
  const cfg = borrador ?? servidor
  const editado = borrador !== null && hayCambios(borrador, servidor)

  const vacios = camposVacios(cfg)
  const builders = useMemo(() => buildersFor(cfg), [cfg])

  const ejemplos: (Ejemplo & { link: ReturnType<typeof builders.closeLead> })[] =
    [
      {
        que: 'Un seguimiento',
        detalle: `work item ${ID_PLANE} · OPS-142`,
        depende: ['plane.baseUrl', 'plane.workspaceSlug', 'plane.projectId'],
        link: builders.planeWorkItem(ID_PLANE, { identificador: 'OPS-142' }),
      },
      {
        que: 'Un cliente en Close',
        detalle: ID_CLOSE,
        depende: ['close.baseUrl'],
        link: builders.closeLead(ID_CLOSE, { nombre: 'Armytech' }),
      },
      {
        que: 'Una suscripción en Stripe',
        detalle: ID_STRIPE_SUB,
        depende: ['stripe.baseUrl'],
        link: builders.stripeSubscription(ID_STRIPE_SUB, {
          nombre: 'Armytech',
        }),
      },
      {
        que: 'Una ficha en la plataforma',
        detalle: ID_PLATAFORMA,
        depende: ['plataforma.baseUrl'],
        link: builders.plataformaCliente(ID_PLATAFORMA, {
          nombre: 'Armytech',
        }),
      },
      {
        que: 'Una query de SEO',
        detalle: `"${QUERY_SEO}"`,
        depende: ['searchConsole.baseUrl', 'searchConsole.resourceId'],
        link: builders.searchConsoleQuery(QUERY_SEO),
      },
    ]

  /** El campo vacío que impide armar este ejemplo, si hay alguno. */
  function faltanteDe(ejemplo: Ejemplo): CampoConfig | undefined {
    return CAMPOS.find(
      (campo) =>
        ejemplo.depende.includes(campo.id) &&
        valorDeCampo(cfg, campo.id).trim().length === 0,
    )
  }

  function editar(campo: CampoId, valor: string) {
    setBorrador((actual) => conCampo(actual ?? servidor, campo, valor))
  }

  async function alPortapapeles(texto: string, ok: string) {
    try {
      await navigator.clipboard.writeText(texto)
      push('ok', ok)
    } catch {
      // Sin permiso de portapapeles el texto sigue visible y seleccionable: el
      // botón es un atajo, no la única forma de sacarlo.
      push('error', 'No pudimos copiar: seleccioná el texto y copialo a mano')
    }
  }

  return (
    <Section
      id={id}
      kicker="BLOQUE 4"
      titulo="A dónde apunta cada link del panel"
      bajada={
        <>
          El panel no guarda URLs de otros sistemas: guarda los ids y arma la URL
          con esta config.{' '}
          {vacios.length === 0 ? (
            <>
              Los <b>{CAMPOS.length} campos</b> están completos, así que ningún
              link sale roto por configuración.
            </>
          ) : (
            /* La concordancia con el mismo helper que el resto del panel: con
               un solo campo vacío esto decía "1 de 8 campos están vacíos, así
               que SEARCH_CONSOLE_RESOURCE_ID no alcanzan". */
            <>
              <b>
                {vacios.length} de {CAMPOS.length} campos
              </b>{' '}
              {plural(vacios.length, 'está vacío', 'están vacíos')}, así que{' '}
              {listaEs(vacios.map((c) => c.envVar))}{' '}
              {plural(vacios.length, 'no alcanza', 'no alcanzan')} para armar una
              URL y{' '}
              {plural(
                vacios.length,
                'ese registro se muestra',
                'esos registros se muestran',
              )}{' '}
              como «sin vincular» en todo el panel.
            </>
          )}
        </>
      }
      acciones={
        editado ? (
          <Button
            variante="secundario"
            icono="sincronizar"
            onClick={() => setBorrador(null)}
          >
            Volver a lo que manda el servidor
          </Button>
        ) : undefined
      }
    >
      <Stack gap="md">
        {GRUPOS.map((grupo) => {
          const campos = CAMPOS.filter((campo) => campo.grupo === grupo)
          if (campos.length === 0) return null

          return (
            <Panel key={grupo} titulo={grupo} padding="md">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {campos.map((campo) => {
                  const valor = valorDeCampo(cfg, campo.id)
                  const vacio = valor.trim().length === 0
                  return (
                    <div key={campo.id} data-campo={campo.id}>
                      <TextField
                        label={campo.label}
                        requerido={campo.requerido}
                        value={valor}
                        onChange={(v) => editar(campo.id, v)}
                        placeholder={campo.ejemplo}
                        ayuda={<code>{campo.envVar}</code>}
                        error={
                          vacio && campo.requerido
                            ? `Sin configurar. ${campo.rompe}`
                            : null
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </Panel>
          )
        })}

        <Panel
          titulo="Cómo queda cada link con esta config"
          subtitulo="Los ejemplos usan los mismos builders del contrato que usa el resto del panel. Son URLs para leer y copiar: los ids son de muestra, así que no se navegan."
          padding="md"
        >
          <Stack as="ul" gap="sm">
            {ejemplos.map((ejemplo) => {
              const falta = faltanteDe(ejemplo)
              const link = ejemplo.link
              return (
                <li
                  key={ejemplo.que}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border pb-2 last:border-b-0 last:pb-0"
                >
                  <span className="min-w-0">
                    <span className="text-meta text-text">{ejemplo.que}</span>{' '}
                    <span className="text-micro text-muted-strong">
                      {ejemplo.detalle}
                    </span>
                  </span>
                  {/* El bloque MUESTRA la URL, no la abre. Un <a> acá llevaría a
                      un 404 del sistema externo, porque el id es de muestra: el
                      builder no puede devolver null cuando el id está falseado,
                      así que el estado "sin vincular" nunca lo cubriría. */}
                  {link === null || falta !== undefined ? (
                    <span
                      data-testid="deeplink-ejemplo-sin-armar"
                      className="inline-flex max-w-full flex-wrap items-center gap-1.5 text-micro text-muted-strong"
                    >
                      <Icon name="sin-vincular" size={11} />
                      <span>
                        No se puede armar: falta{' '}
                        <code className="text-warning-text">
                          {falta?.envVar ?? 'un campo de arriba'}
                        </code>
                      </span>
                      {falta !== undefined ? (
                        <Button
                          variante="chico"
                          onClick={() => enfocarCampo(falta.id)}
                        >
                          Completar {falta.envVar}
                        </Button>
                      ) : null}
                    </span>
                  ) : (
                    <span className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-1.5">
                      <code
                        data-testid="deeplink-ejemplo"
                        className="min-w-0 break-all rounded border border-border bg-n100 px-1.5 py-0.5 text-micro text-text"
                      >
                        {link.url}
                      </code>
                      <Button
                        variante="chico"
                        icono="link"
                        onClick={() =>
                          void alPortapapeles(
                            link.url,
                            `URL copiada: ${ejemplo.que.toLowerCase()}`,
                          )
                        }
                      >
                        Copiar
                        <span className="sr-only"> la URL de: {ejemplo.que}</span>
                      </Button>
                    </span>
                  )}
                </li>
              )
            })}
          </Stack>
        </Panel>

        <Card padding="md" hundida punteada>
          <Stack gap="sm">
            <h3 className="inline-flex items-center gap-2 text-titulo text-text">
              <Icon name="engranaje" size={14} />
              Para que quede aplicado
            </h3>
            <p className="max-w-prosa text-meta text-muted-strong">
              Esta config la resuelve el Worker por variable de entorno y llega al
              front en <code>GET /api/config</code>. El panel la muestra y te deja
              probarla, pero <b className="font-semibold text-text">no la guarda</b>
              : una copia en base sería una segunda verdad que puede contradecir al
              entorno. Aplicá estas variables en el entorno del Worker y volvé a
              desplegar.
            </p>
            {editado ? (
              <StatusPill severidad="warning" size="sm">
                Estás viendo una config editada que todavía no está aplicada
              </StatusPill>
            ) : (
              <StatusPill severidad="ok" size="sm">
                Esto es exactamente lo que hoy sirve el servidor
              </StatusPill>
            )}
            <Rule spacing="none" punteada />
            <pre
              data-testid="deeplinks-env"
              className="overflow-x-auto rounded border border-border bg-surface p-2 text-micro leading-relaxed text-text"
            >
              {envSnippet(cfg)}
            </pre>
            <div>
              <Button
                variante="secundario"
                icono="link"
                onClick={() =>
                  void alPortapapeles(
                    envSnippet(cfg),
                    'Variables copiadas al portapapeles',
                  )
                }
              >
                Copiar las variables
              </Button>
            </div>
          </Stack>
        </Card>
      </Stack>
    </Section>
  )
}
